// Real-time privacy face masking for cam-to-cam (Premium).
// Routes a raw camera stream through a canvas that either blurs the face
// region or overlays an animated masquerade mask that tracks the face,
// then exposes the processed stream via canvas.captureStream().
import { FaceLandmarker, FilesetResolver } from "@mediapipe/tasks-vision";

const MASK_SRC = "/masquerade-mask.png";
let landmarkerPromise = null;

export const preloadFaceMask = () => getLandmarker().catch(() => {});

async function getLandmarker() {
  if (!landmarkerPromise) {
    landmarkerPromise = (async () => {
      const resolver = await FilesetResolver.forVisionTasks(
        "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@1.0.1/wasm"
      );
      return FaceLandmarker.createFromOptions(resolver, {
        baseOptions: {
          modelAssetPath:
            "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task",
          delegate: "GPU",
        },
        runningMode: "VIDEO",
        numFaces: 1,
      });
    })();
  }
  return landmarkerPromise;
}

function loadMaskImage() {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = MASK_SRC;
  });
}

export class FaceMaskProcessor {
  constructor() {
    this.mode = "mask"; // 'mask' | 'blur'
    this.running = false;
    this.raf = null;
    this.video = null;
    this.canvas = null;
    this.ctx = null;
    this.landmarker = null;
    this.maskImg = null;
    this.lastVideoTime = -1;
  }

  setMode(mode) {
    this.mode = mode;
  }

  async start(rawStream, mode = "mask") {
    this.mode = mode;
    [this.landmarker, this.maskImg] = await Promise.all([getLandmarker(), loadMaskImage()]);

    const video = document.createElement("video");
    video.autoplay = true;
    video.muted = true;
    video.playsInline = true;
    video.srcObject = new MediaStream(rawStream.getVideoTracks());
    await video.play().catch(() => {});
    this.video = video;

    const w = video.videoWidth || 640;
    const h = video.videoHeight || 480;
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    this.canvas = canvas;
    this.ctx = canvas.getContext("2d");

    this.running = true;
    this._loop();

    const out = new MediaStream();
    canvas.captureStream(30).getVideoTracks().forEach((t) => out.addTrack(t));
    rawStream.getAudioTracks().forEach((t) => out.addTrack(t));
    return out;
  }

  _loop = () => {
    if (!this.running) return;
    const { video, ctx, canvas } = this;
    if (video && video.readyState >= 2) {
      const w = canvas.width;
      const h = canvas.height;
      ctx.drawImage(video, 0, 0, w, h);

      let landmarks = null;
      const ts = performance.now();
      if (video.currentTime !== this.lastVideoTime) {
        this.lastVideoTime = video.currentTime;
        try {
          const res = this.landmarker.detectForVideo(video, ts);
          landmarks = res?.faceLandmarks?.[0] || null;
          this._lastLandmarks = landmarks;
        } catch {
          landmarks = this._lastLandmarks || null;
        }
      } else {
        landmarks = this._lastLandmarks || null;
      }

      if (landmarks) {
        if (this.mode === "blur") this._drawBlur(landmarks, w, h);
        else this._drawMask(landmarks, w, h);
      } else if (this.mode === "blur") {
        // No face detected — blur whole frame so identity is never exposed.
        ctx.save();
        ctx.filter = "blur(22px)";
        ctx.drawImage(video, 0, 0, w, h);
        ctx.restore();
      }
    }
    this.raf = requestAnimationFrame(this._loop);
  };

  _bbox(landmarks, w, h) {
    let minX = 1, minY = 1, maxX = 0, maxY = 0;
    for (const p of landmarks) {
      if (p.x < minX) minX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.x > maxX) maxX = p.x;
      if (p.y > maxY) maxY = p.y;
    }
    return {
      x: minX * w,
      y: minY * h,
      w: (maxX - minX) * w,
      h: (maxY - minY) * h,
    };
  }

  _drawBlur(landmarks, w, h) {
    const b = this._bbox(landmarks, w, h);
    const pad = b.w * 0.35;
    const x = Math.max(0, b.x - pad);
    const y = Math.max(0, b.y - pad * 1.4);
    const bw = Math.min(w - x, b.w + pad * 2);
    const bh = Math.min(h - y, b.h + pad * 2.2);
    const ctx = this.ctx;
    ctx.save();
    ctx.beginPath();
    ctx.ellipse(x + bw / 2, y + bh / 2, bw / 2, bh / 2, 0, 0, Math.PI * 2);
    ctx.clip();
    ctx.filter = "blur(20px)";
    ctx.drawImage(this.video, x, y, bw, bh, x, y, bw, bh);
    ctx.restore();
  }

  _drawMask(landmarks, w, h) {
    if (!this.maskImg) return this._drawBlur(landmarks, w, h);
    // Outer eye corners: 33 and 263 in the MediaPipe face mesh.
    const le = landmarks[33];
    const re = landmarks[263];
    if (!le || !re) return;
    const x1 = le.x * w, y1 = le.y * h;
    const x2 = re.x * w, y2 = re.y * h;
    const cx = (x1 + x2) / 2;
    const cy = (y1 + y2) / 2;
    const eyeDist = Math.hypot(x2 - x1, y2 - y1);
    const angle = Math.atan2(y2 - y1, x2 - x1);
    const maskW = eyeDist * 2.25;
    const maskH = maskW * (this.maskImg.height / this.maskImg.width);
    const ctx = this.ctx;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(angle);
    ctx.drawImage(this.maskImg, -maskW / 2, -maskH / 2 - maskH * 0.05, maskW, maskH);
    ctx.restore();
  }

  stop() {
    this.running = false;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = null;
    if (this.video) {
      this.video.pause();
      this.video.srcObject = null;
      this.video = null;
    }
    this.canvas = null;
    this.ctx = null;
    this._lastLandmarks = null;
  }
}
